import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Check, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export interface EnhancePromptDiffProps {
  before: string;
  after: string;
  onApply: (finalText: string) => void;
  onDiscard: () => void;
}

/**
 * Diff view side-by-side (drawer/overlay) exigido pelo spec.
 * Desktop: duas colunas lado-a-lado.
 * Mobile: stack vertical.
 * Permite editar o resultado antes de aplicar.
 */
export function EnhancePromptDiff({
  before,
  after,
  onApply,
  onDiscard,
}: EnhancePromptDiffProps) {
  const [editMode, setEditMode] = useState(false);
  const [editableAfter, setEditableAfter] = useState(after);
  const applyButtonRef = useRef<HTMLButtonElement>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Foco automatico no botao Aplicar ao abrir
  useEffect(() => {
    applyButtonRef.current?.focus();
  }, []);

  // Se estado de edicao muda, foca o textarea
  useEffect(() => {
    if (editMode) {
      editTextareaRef.current?.focus();
    }
  }, [editMode]);

  // ESC fecha o overlay
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onDiscard();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDiscard]);

  // Mantem refined sincronizado com prop after se nao estiver editando
  useEffect(() => {
    if (!editMode) setEditableAfter(after);
  }, [after, editMode]);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Diferenca entre prompt original e refinado"
      data-testid="enhance-diff-overlay"
    >
      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.18 }}
        className="neu-card flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-border bg-card"
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Refinar prompt
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Reveja o resultado antes de aplicar. Nada e enviado ao chat ainda.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onDiscard}
            aria-label="Descartar refinamento"
            className="h-9 w-9"
          >
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="grid flex-1 grid-cols-1 gap-0 overflow-hidden md:grid-cols-2">
          <section
            className="flex min-h-[200px] flex-col border-b border-border md:border-b-0 md:border-r"
            aria-labelledby="enhance-before-label"
          >
            <div className="flex items-center justify-between border-b border-border/60 bg-muted/40 px-4 py-2">
              <span
                id="enhance-before-label"
                className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Antes
              </span>
              <span className="text-[10px] text-muted-foreground">
                {before.length} caracteres
              </span>
            </div>
            <div
              className="flex-1 overflow-auto whitespace-pre-wrap break-words px-4 py-3 text-xs text-muted-foreground"
              data-testid="enhance-diff-before"
            >
              {before}
            </div>
          </section>

          <section
            className="flex min-h-[200px] flex-col"
            aria-labelledby="enhance-after-label"
          >
            <div className="flex items-center justify-between border-b border-border/60 bg-primary/5 px-4 py-2">
              <span
                id="enhance-after-label"
                className="text-xs font-semibold uppercase tracking-wider text-primary"
              >
                Depois
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground">
                  {editableAfter.length} caracteres
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditMode((v) => !v)}
                  aria-label={editMode ? "Concluir edicao" : "Editar refinamento"}
                  aria-pressed={editMode}
                  className="h-7 w-7"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            {editMode ? (
              <Textarea
                ref={editTextareaRef}
                value={editableAfter}
                onChange={(e) => setEditableAfter(e.target.value)}
                aria-label="Editar prompt refinado"
                className="flex-1 resize-none border-0 bg-transparent px-4 py-3 text-xs focus-visible:ring-0"
              />
            ) : (
              <div
                className="flex-1 overflow-auto whitespace-pre-wrap break-words px-4 py-3 text-xs text-foreground"
                data-testid="enhance-diff-after"
              >
                {editableAfter}
              </div>
            )}
          </section>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            onClick={onDiscard}
            aria-label="Descartar e manter prompt original"
          >
            Descartar
          </Button>
          <Button
            ref={applyButtonRef}
            type="button"
            onClick={() => onApply(editableAfter)}
            aria-label="Aplicar prompt refinado"
            data-testid="enhance-apply-button"
            className="gap-1.5"
          >
            <Check className="h-4 w-4" />
            Aplicar
          </Button>
        </footer>
      </motion.div>
    </div>
  );
}
